function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import endpoints from "../../endpoints";
import { call, toast } from "../audiences/api";

/*
 * Content Hub ↔ brand library (Plunk DAM, PlunkDocs specs/PLUNK_DAM_2026-10-26.md §16, 2026-10-27). Loose integration:
 * picking a library work copies its current version into Content Hub once per version (POST /v1/dam/content-hub/assets)
 * and hands back a normal Content Hub asset, so every editor, piece and ship step works unchanged. Pieces already made
 * keep the version they used; the next pick after a replace in the library brings the new one. "Save to brand library"
 * (POST /v1/dam/content-hub/save) goes the other way, with the AI recipe when the asset was made by AI.
 * Workspaces without a brand library answer 503 dam_not_provisioned: damAvailable() is false and nothing shows.
 */

const DAM = `${String(endpoints.product && endpoints.product.root || "")}/dam`;
const idStr = v => v === undefined || v === null ? v : String(v);
let availability = null;
/** Is the brand library on for this workspace? Cached for the page. */
export function damAvailable() {
  if (!availability) availability = call(`${DAM}/works`, {
    query: {
      $top: 1
    }
  }).then(() => true).catch(() => false);
  return availability;
}

/** Copy a library work into Content Hub (or reuse the copy of its current version) → a Content Hub asset. */
export async function damToContent(workId) {
  const d = await call(`${DAM}/content-hub/assets`, {
    method: "POST",
    body: {
      workId: String(workId)
    }
  });
  const a = d && d.contentAsset || {};
  return {
    ...a,
    id: idStr(a.id || a.Id),
    source: "brand_library",
    reused: !!(d && d.reused),
    damVersion: d && d.version
  };
}

/** Save a Content Hub asset to the brand library → { workId, duplicate }. */
export const damSaveFromContent = (contentAssetId, title) => call(`${DAM}/content-hub/save`, {
  method: "POST",
  body: {
    contentAssetId: String(contentAssetId),
    ...(title ? {
      title
    } : {})
  }
});

/** Which library work an asset came from: { workId, version, currentVersion, behind } or null. */
export const damLinkOf = contentAssetId => call(`${DAM}/content-hub/assets/${encodeURIComponent(contentAssetId)}`).catch(() => null);
const errText = e => e && e.data && (e.data.error || e.data.message) || e && e.message || "Something went wrong";

/**
 * A grid of brand library works to pick from. Props: accept ("image" | "video" | "any"), onPicked(contentAsset)
 * after the copy into Content Hub, disabled, hint. Search by words or by meaning (the library's own search).
 */
export class BrandLibraryPane extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      q: "",
      items: undefined,
      thumbs: {},
      busy: null
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "kind", () => this.props.accept === "image" ? "image" : this.props.accept === "video" ? "video" : undefined);
    _defineProperty(this, "load", async q => {
      try {
        const d = await call(`${DAM}/works`, {
          query: {
            $top: 60,
            ...(q ? {
              q
            } : {}),
            ...(this.kind() ? {
              kind: this.kind()
            } : {})
          }
        });
        if (!this._alive || (this.state.q || "").trim() !== q) return;
        const items = (d && d.items || []).filter(w => !w.archived);
        this.setState({
          items
        });
        const ids = items.filter(w => w.kind === "image" && !this.state.thumbs[w.id]).map(w => w.id);
        if (ids.length) {
          // new thumbnails render on first request; a cold start can outlast the gateway, so try once more
          let t = await call(`${DAM}/thumbs`, {
            method: "POST",
            body: {
              workIds: ids
            }
          }).catch(() => null);
          if (!t || !t.items || Object.keys(t.items).length < ids.length) {
            await new Promise(r => setTimeout(r, 2500));
            t = await call(`${DAM}/thumbs`, {
              method: "POST",
              body: {
                workIds: ids
              }
            }).catch(() => t);
          }
          if (this._alive && t && t.items) this.setState({
            thumbs: {
              ...this.state.thumbs,
              ...t.items
            }
          });
        }
      } catch (e) {
        if (this._alive) this.setState({
          items: null
        });
      }
    });
    _defineProperty(this, "search", q => {
      this.setState({
        q
      });
      clearTimeout(this._t);
      this._t = setTimeout(() => this.load(q.trim()), 400);
    });
    _defineProperty(this, "pick", async w => {
      if (this.state.busy) return;
      this.setState({
        busy: w.id
      });
      try {
        const a = await damToContent(w.id);
        if (this.props.onPicked) this.props.onPicked(a);
      } catch (e) {
        const code = e && e.data && e.data.code;
        toast(code === "approval_required" ? "That picture needs approval in the brand library first." : code === "rights_blocked" ? errText(e) : `Couldn't add it: ${errText(e)}`);
      }
      if (this._alive) this.setState({
        busy: null
      });
    });
  }
  componentDidMount() {
    this.load("");
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._t);
  }
  render() {
    const {
      disabled,
      hint
    } = this.props;
    const {
      q,
      items,
      thumbs,
      busy
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "sq-picker bl-pane"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sq-picker-tools"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sq-search"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "search"), /*#__PURE__*/React.createElement("input", {
      value: q,
      placeholder: "Search the brand library, or describe it",
      disabled: disabled,
      onChange: e => this.search(e.target.value)
    })), hint ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, hint) : /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "Picking one adds it to your assets.")), items === undefined ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Loading the brand library\u2026") : items === null ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "The brand library isn't available right now.") : !items.length ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, q ? "Nothing matches." : "The brand library is empty.") : /*#__PURE__*/React.createElement("div", {
      className: "sq-picker-grid"
    }, items.map(w => {
      const thumb = thumbs[w.id];
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: w.id,
        className: "sq-pa" + (busy === w.id ? " on" : ""),
        disabled: disabled || !!busy,
        title: w.title || "",
        onClick: () => this.pick(w)
      }, /*#__PURE__*/React.createElement("span", {
        className: "im"
      }, thumb ? /*#__PURE__*/React.createElement("img", {
        src: thumb,
        alt: "",
        loading: "lazy"
      }) : /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, w.kind === "video" ? "play_circle" : w.kind === "doc" ? "description" : "image")), busy === w.id ? /*#__PURE__*/React.createElement("i", {
        className: "n"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        style: {
          fontSize: 14
        }
      }, "hourglass_top")) : null, /*#__PURE__*/React.createElement("span", {
        className: "kind"
      }, "library", w.version > 1 ? ` · v${w.version}` : ""), /*#__PURE__*/React.createElement("span", {
        className: "cap"
      }, w.title || "Untitled"));
    })));
  }
}